import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { env } from "@/lib/env";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSettingsCategories, useSettingsItems } from "../hooks/use-settings-items";
import { FIELD_REGISTRY } from "./registry";
import type { ResolvedEntry } from "./types";

/**
 * Resolve all searchable entries (section-level + field-level) into a flat
 * list with translated label/description/keywords. Section entries are
 * auto-derived from `useSettingsItems`; field entries come from FIELD_REGISTRY.
 */
export function useSearchEntries(): readonly ResolvedEntry[] {
  const { t } = useTranslation("settings");
  const items = useSettingsItems();
  const categories = useSettingsCategories();
  const { user } = useAuthStore();
  const isAdmin = user?.role.some((r) => r === "ADMIN" || r === "SUPER_ADMIN") ?? false;
  const showExperimental = env.flags.showExperimental;

  return useMemo(() => {
    const sectionMap = new Map(items.map((item) => [item.id, item]));

    const sectionEntries: ResolvedEntry[] = items.map((item) => ({
      kind: "section",
      sectionId: item.id,
      label: item.title,
      description: item.description,
      keywords: [],
      breadcrumb: categories[item.category]?.title ?? "",
    }));

    const fieldEntries: ResolvedEntry[] = FIELD_REGISTRY.filter(
      (field) => !field.visible || field.visible({ isAdmin, showExperimental })
    ).map((field) => {
      const section = sectionMap.get(field.sectionId);
      const sectionTitle = section?.title ?? "";
      const categoryTitle = section ? (categories[section.category]?.title ?? "") : "";
      const breadcrumb =
        categoryTitle && sectionTitle ? `${categoryTitle} → ${sectionTitle}` : sectionTitle;

      const keywordsRaw = field.keywordsKey ? t(field.keywordsKey) : "";
      const keywords = keywordsRaw
        ? keywordsRaw
            .split(",")
            .map((keyword) => keyword.trim())
            .filter(Boolean)
        : [];

      return {
        kind: "field",
        sectionId: field.sectionId,
        fieldId: field.id,
        anchor: field.anchor,
        label: t(field.labelKey),
        description: field.descriptionKey ? t(field.descriptionKey) : "",
        keywords,
        breadcrumb,
      };
    });

    return [...sectionEntries, ...fieldEntries];
  }, [t, items, categories, isAdmin, showExperimental]);
}
